import type { QaCatalogCheck, QaCatalogStep } from "../api/qaOrchestrator";

// A catalog step as a person reads it. The catalog sends each step as its
// action and a one-line summary (orchestrator catalog.ts, summarizeStep);
// this turns that line back into words ("Press E", "Run /give …", "Wait 2 s")
// and its expectations into labelled facts.

/** One step in words: `label`, then `code` (a command, item or text), a `key` cap, a muted `detail` and labelled `facts`. */
export type StepWords = {
  label: string;
  key?: string;
  code?: string;
  detail?: string;
  facts?: { name: string; value: string }[];
  /** Waits and other filler, shown quieter. */
  quiet?: boolean;
};

const cap = (s: string) => (s ? s[0]!.toUpperCase() + s.slice(1) : s);

/** "e" → "E", "escape" → "Escape", "shift" → "Shift". */
export const keyName = (key: string) => (key.length === 1 ? key.toUpperCase() : cap(key));

/** 2500 → "2.5 s", 400 → "400 ms". */
export function duration(ms: number): string {
  if (!Number.isFinite(ms)) return `${ms} ms`;
  return ms >= 1000 ? `${+(ms / 1000).toFixed(2)} s` : `${ms} ms`;
}

/** A JSON expectation in words: {"min":1,"max":360} → "1 – 360", ["night_vision"] → "night_vision". */
export function prettyValue(v: unknown): string {
  if (Array.isArray(v)) return v.map(prettyValue).join(", ");
  if (v && typeof v === "object") {
    const o = v as Record<string, unknown>;
    const keys = Object.keys(o);
    if (keys.length && keys.every((k) => k === "min" || k === "max")) {
      if (o.min !== undefined && o.max !== undefined) return o.min === o.max ? `${o.min}` : `${o.min} – ${o.max}`;
      return o.min !== undefined ? `≥ ${o.min}` : `≤ ${o.max}`;
    }
    return keys.map((k) => `${k} ${prettyValue(o[k])}`).join("; ");
  }
  return String(v);
}

/** "expectNoEffect" → "no effect", "withinMs" → "within ms". */
const factName = (k: string) => k.replace(/^expect(?=[A-Z])/, "").replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase();

/** The ` key=value` pairs the summary ends with, values parsed from JSON where they are. */
export function parseFacts(rest: string): { name: string; value: string }[] {
  const out: { name: string; value: string }[] = [];
  const re = /(?:^|\s)(\w+)=/g;
  const marks: { key: string; start: number; end: number }[] = [];
  for (let m; (m = re.exec(rest)); ) marks.push({ key: m[1]!, start: m.index, end: m.index + m[0].length });
  marks.forEach((mk, i) => {
    const raw = rest.slice(mk.end, marks[i + 1]?.start ?? rest.length).trim();
    let value: string;
    try { value = prettyValue(JSON.parse(raw)); } catch { value = raw; }
    // "withinMs=2000" → "within 2 s".
    if (/[a-z]Ms$/.test(mk.key) && /^\d+$/.test(raw)) out.push({ name: factName(mk.key.slice(0, -2)), value: duration(Number(raw)) });
    else out.push({ name: factName(mk.key), value });
  });
  return out;
}

const xy = (s: string) => s.match(/\((-?[\d.]+), (-?[\d.]+)\)/);
const after = (summary: string, prefix: string) => (summary.startsWith(prefix) ? summary.slice(prefix.length) : summary);
const slash = (cmd: string) => (cmd.startsWith("/") ? cmd : `/${cmd}`);

const POINTER: Record<string, string> = {
  click: "Click",
  double_click: "Double-click",
  right_click: "Right-click",
  move_mouse: "Move the mouse",
  attack: "Attack",
  sneak_use: "Sneak and use",
  sneak_attack: "Sneak and attack",
};

export function describeStep(step: Pick<QaCatalogStep, "action" | "summary" | "checks">): StepWords {
  const s = step.summary;
  switch (step.action) {
    case "click":
    case "double_click":
    case "right_click":
    case "move_mouse":
    case "attack":
    case "sneak_use":
    case "sneak_attack": {
      const p = xy(s);
      const label = POINTER[step.action]!;
      return p ? { label: step.action === "move_mouse" ? `${label} to` : `${label} at`, detail: `${p[1]}, ${p[2]}` } : { label };
    }
    case "key":
      return { label: "Press", key: keyName(after(s, "key ")) };
    case "key_down":
      return { label: "Hold", key: keyName(after(s, "key down ")) };
    case "key_up":
      return { label: "Release", key: keyName(after(s, "key up ")) };
    case "hotbar":
      return { label: `Select hotbar slot ${after(s, "hotbar slot ")}` };
    case "wait": {
      const ms = Number(after(s, "wait ").replace(/ ms$/, ""));
      return { label: `Wait ${duration(ms)}`, quiet: true };
    }
    case "type":
      return { label: "Type", code: after(s, "type ").replace(/^"|"$/g, "") };
    case "walk": {
      const m = s.match(/^walk (\S+) for (\d+) ms$/);
      return m ? { label: "Walk", key: keyName(m[1]!), detail: `for ${duration(Number(m[2]))}` } : { label: cap(s) };
    }
    case "drag":
      return { label: "Drag", detail: after(s, "drag ") };
    case "scroll":
      return { label: "Scroll", detail: after(s, "scroll ") };
    case "aim":
      return { label: "Turn the camera", detail: after(s, "aim ") };
    case "command":
      return { label: "Run", code: slash(after(s, "command: ")) };
    case "server_command":
      return { label: "Run on the server", code: slash(after(s, "server command: ")) };
    case "give_item": {
      const m = s.match(/^give (\S+) × (.+)$/);
      return m ? { label: "Give", code: m[2], detail: `× ${m[1]}` } : { label: cap(s) };
    }
    case "clear_inventory":
      return { label: "Clear the inventory" };
    case "restart_server":
      return { label: "Restart the server" };
    case "observe":
      return { label: "Observe", quiet: true };
    case "navigate_to":
      return { label: "Walk to", code: after(s, "navigate to ") };
    case "look_at":
      return { label: "Look at", code: after(s, "look at ") };
    case "place_block": {
      const m = s.match(/^place (\S+) at (.+)$/);
      return m ? { label: "Place", code: m[1], detail: `at ${m[2]}` } : { label: cap(s) };
    }
    case "inspect_block": {
      const m = s.match(/^block at (.+) is (.+)$/);
      return m ? { label: `Check the block at ${m[1]} is`, code: m[2] } : { label: cap(s) };
    }
    case "inspect_entity": {
      const m = s.match(/^(\S+) (present|absent)$/);
      return m ? { label: "Check", code: m[1], detail: m[2] === "present" ? "is there" : "is not there" } : { label: cap(s) };
    }
    case "set_world_property":
      return { label: "Set world property", code: after(s, "set world property ") };
    case "get_world_property":
      return { label: "Check world property", code: after(s, "world property ") };
    case "screenshot": {
      const n = step.checks?.length ?? 0;
      return { label: "Screenshot", detail: n === 1 ? "1 check" : `${n} checks` };
    }
    case "inspect_player":
      return { label: "Check the player", facts: parseFacts(after(s, "inspect player:")) };
    case "watch_player": {
      const m = s.match(/^watch player (\d+) ticks(?: after "(.*?)")?:(.*)$/);
      if (!m) return { label: cap(s) };
      return { label: `Watch the player for ${m[1]} ticks${m[2] ? " after" : ""}`, code: m[2] ? slash(m[2]) : undefined, facts: parseFacts(m[3]!) };
    }
    case "inspect_entities": {
      const m = s.match(/^inspect (\S+) entities:(.*)$/);
      return m ? { label: "Check the", code: m[1], detail: "entities", facts: parseFacts(m[2]!) } : { label: cap(s) };
    }
    default: {
      const i = s.indexOf(" ");
      return i > 0 ? { label: cap(step.action.replace(/_/g, " ")), facts: parseFacts(s.slice(i)) } : { label: cap(s) };
    }
  }
}

/** "1104×979 at (722, 187)", or "Whole screen". */
export const regionLabel = (r: QaCatalogCheck["region"]) => (r ? `${r.width}×${r.height} at (${r.x}, ${r.y})` : "Whole screen");

/** The region's width / height when both are numbers, for the thumbnail's shape. */
export function regionAspect(r: QaCatalogCheck["region"]): number | null {
  if (!r || typeof r.width !== "number" || typeof r.height !== "number" || r.width <= 0 || r.height <= 0) return null;
  return r.width / r.height;
}
